import { noop, type Unsubscribe } from '@amazon/vinyl-util'
import { getDeviceConfig } from '@/device/deviceConfig'

/**
 * Lazy entry point for spatial-navigation key bindings. The actual
 * implementation (scoring, key handling, focus walks) lives in
 * `./spatialNav` and is only imported via `await import()` when
 * the device config asks for spatial nav. Vite splits that into
 * its own chunk, so non-TV bundles never download the code.
 *
 * Returns a synchronous Unsubscribe so callers can register the
 * teardown immediately. Internally, the actual unsub becomes
 * available once the dynamic import resolves; if disposal happens
 * before the chunk loads we mark `disposed` and let the loader
 * skip the bind.
 */
export function initializeSpatialKeyBindings(): Unsubscribe {
    const { keyMap, useSpatialNavigation } = getDeviceConfig()
    if (!useSpatialNavigation) return noop

    let disposed = false
    let teardown: Unsubscribe | null = null

    void import('./spatialNav').then(({ bindSpatialKeys }) => {
        if (disposed) return
        teardown = bindSpatialKeys(keyMap)
    })

    return () => {
        disposed = true
        if (teardown) {
            teardown()
            teardown = null
        }
    }
}
